import { motion } from 'framer-motion';
import { useCallback } from 'react';
import { Link } from 'react-scroll';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import '../index.css';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.6,
      delayChildren: 0.4,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const logoVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 30 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

function Home() {
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesLoaded = useCallback(async (container) => {
    await console.log(container);
  }, []);

  return (
    <section
      id="Home"
      className="relative h-[100vh] overflow-hidden bg-gray-50 flex justify-center flex-col px-4 sm:px-6 md:px-12 lg:px-20 xl:px-24 font-primary"
    >
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none"
        options={{
          background: {},
          fpsLimit: 300,
          interactivity: {
            events: {
              onClick: {
                enable: true,
                mode: 'push',
              },
              resize: true,
            },
            modes: {
              push: {
                quantity: 0,
              },
              repulse: {
                distance: 150,
                duration: 0.4,
              },
            },
          },
          particles: {
            color: {
              value: '#000000',
            },
            links: {
              color: '#000000',
              distance: 150,
              enable: true,
              opacity: 1,
              width: 1,
            },
            move: {
              direction: 'none',
              enable: true,
              outModes: {
                default: 'bounce',
              },
              random: false,
              speed: 2,
              straight: false,
            },
            number: {
              density: {
                enable: true,
                area: 1000,
              },
              value: 10,
            },
            opacity: {
              value: 0.5,
            },
            shape: {
              type: 'circle',
            },
            size: {
              value: { min: 1, max: 5 },
            },
          },
          detectRetina: true,
        }}
      />

      <motion.div
        className="flex flex-col items-center justify-center z-20"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Logo and Brand */}
        <motion.div
          className="pb-4 sm:pb-6 lg:pb-8 xl:pb-10 relative p-2 w-fit rounded-lg bg-gray-50 z-20 flex flex-col sm:flex-row items-center gap-2 sm:gap-3 lg:gap-4"
          variants={logoVariants}
        >
          <motion.img
            src="/AR5_.png"
            alt="AR5 logo"
            className="h-12 xs:h-14 sm:h-16 md:h-20 lg:h-24 xl:h-28"
            // whileHover={{ scale: 1.005 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          />
          <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight">
            <span className="text-black">AR5 </span>
            <span className="text-[#AFAFAF]">Labs</span>
          </h1>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          className="text-base sm:text-lg lg:text-xl xl:text-2xl bg-gray-50 text-center p-2 font-semibold relative z-20 text-black leading-tight max-w-xl sm:max-w-2xl md:max-w-3xl lg:max-w-4xl"
          variants={itemVariants}
        >
          
          Software innovation lab delivering proprietary platforms, technical strategy, and product development
        </motion.h1>

        <br />

        {/* Subheadline */}
        <motion.p
          className="bg-gray-50 text-sm sm:text-base lg:text-lg xl:text-xl text-center p-2 font-medium text-gray-500 relative z-20 w-fit max-w-sm sm:max-w-lg md:max-w-xl lg:max-w-2xl"
          variants={itemVariants}
        >

                    {/* Consulting and product lab that builds proprietary software and provides technical guidance. */}
                    Helping your organization integrate AI and analytics with clarity and
                    confidence
        </motion.p>

        {/* CTA Button */}
        <motion.div className="w-fit relative z-20 mt-3 sm:mt-4 lg:mt-6 bg-gray-50 p-2" variants={itemVariants}>
          <Link
            to="Contact"
            smooth={true}
            duration={450}
            offset={-64}
            spy={true}
          >
            <motion.button
              className="bg-[#0D5192] text-sm sm:text-base lg:text-lg py-2 sm:py-2.5 lg:py-3 px-5 sm:px-6 lg:px-8 text-white rounded-xl font-medium cursor-pointer"
              whileHover={{
                scale: 1.01,
                backgroundColor: '#073E72',
                boxShadow: '0 4px 20px rgba(13, 81, 146, 0.15)',
              }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              Get in Touch
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default Home;
